/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useServerStats.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the system module.
────────────────────────────────────────────────────────────────*/


import { useState, useCallback } from 'react';
import { SystemConfig } from '../../../types/system';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DiskEntry {
  filesystem: string;
  size: string;
  used: string;
  available: string;
  usePercent: number;
  mountpoint: string;
}

export interface StorageEntry {
  filesystem: string;
  total: number;
  used: number;
  available: number;
  percentage: number;
  mountPoint: string;
}

export interface ServerStats {
  cpu: { usage: number; percentage: number };
  memory: { total: number; used: number; percentage: number };
  storage: StorageEntry[];
  timestamp: string;
}

interface UseServerStatsReturn {
  serverStats: ServerStats;
  /** Refreshes server stats from the monitoring API. Updates currentDate/Time on formData. */
  loadServerStats: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------

const DEFAULT_STATS: ServerStats = {
  cpu: { usage: 0, percentage: 0 },
  memory: { total: 0, used: 0, percentage: 0 },
  storage: [
    { filesystem: '/dev/sda1', total: 232, used: 17, available: 215, percentage: 7.15, mountPoint: '/' },
    { filesystem: '/dev/sda2', total: 0.5, used: 0.006, available: 0.5, percentage: 1.14, mountPoint: '/boot/efi' },
  ],
  timestamp: new Date().toISOString(),
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useServerStats
 *
 * Fetches system resource statistics from the monitoring API and provides a
 * `loadServerStats()` function that can be called to refresh the data.
 *
 * @param setFormData - React setState setter for SystemConfig; only the live
 *   time fields (currentDate / currentTime) are updated from the API response.
 */
export function useServerStats(
  setFormData: React.Dispatch<React.SetStateAction<SystemConfig>>,
): UseServerStatsReturn {
  const [serverStats, setServerStats] = useState<ServerStats>(DEFAULT_STATS);

  /** Parses a size string like "121G" to a floating-point number of GB */
  const parseSize = (s: string): number => parseFloat(s.replace(/[A-Z]/gi, '')) || 0;

  const loadServerStats = useCallback(async () => {
    try {
      const monitoringUrl = `${window.location.protocol}//${window.location.host}/monitoring-api/api/system/stats`;
      const response = await fetch(monitoringUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      if (!result.success || !result.data) return;

      const { data } = result;

      // Map disk entries from the monitoring API format to our internal format
      const storage: StorageEntry[] = (data.disk ?? []).map((d: DiskEntry) => ({
        filesystem: d.filesystem,
        total: parseSize(d.size),
        used: parseSize(d.used),
        available: parseSize(d.available),
        percentage: d.usePercent,
        mountPoint: d.mountpoint,
      }));

      setServerStats({
        cpu: { usage: data.cpu?.usage ?? 0, percentage: data.cpu?.usage ?? 0 },
        memory: {
          total: data.memory?.total ?? 0,
          used: data.memory?.used ?? 0,
          percentage: data.memory?.usage ?? 0,
        },
        storage,
        timestamp: new Date().toISOString(),
      });

      // Update only the live display fields — timezone/NTP come from the DB
      if (data.time) {
        setFormData((prev) => ({
          ...prev,
          currentDate: data.time.currentDate ?? prev.currentDate,
          currentTime: data.time.currentTime ?? prev.currentTime,
        }));
      }
    } catch (error) {
      // Silently keep defaults; the UI will continue showing last-known values
      console.warn('[useServerStats] Failed to load server stats:', error);
    }
  }, [setFormData]);

  return { serverStats, loadServerStats };
}
